import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared research library contracts (design §8). A research_item is the queryable
// projection + pgvector anchor for a published research artifact. Only PUBLISHED
// artifacts live in the almanac-wiki git repo (markdown = source of truth); the
// raw originating note text never enters git (privacy invariant, see notes.ts).
// ---------------------------------------------------------------------------
export const RESEARCH_STATUSES = ['draft', 'published', 'archived'];
/**
 * What kind of artifact this is. 'research' = produced by a research pass over
 * external sources; 'finding' = distilled from an assistant conversation,
 * capturing evidence that accumulates (by topic tag) toward a future spec.
 */
export const RESEARCH_KINDS = ['research', 'finding'];
/** Lightweight cross-link between research artifacts (derivation edges). */
export const ResearchLinkRefSchema = z
    .object({
    research_id: z.string().min(1),
    title: z.string(),
    slug: z.string(),
})
    .strict();
/**
 * Who can read a research item. 'private' = owner only, never wiki-ingested;
 * 'internal' = any authenticated user (the default); 'public' = also served
 * unauthenticated (API `/api/public/research/:slug`, SPA `/p/:slug`). Anything
 * shared (internal/public) is eligible for almanac-wiki git ingest — sharing
 * implies wiki eligibility.
 */
export const RESEARCH_VISIBILITIES = ['private', 'internal', 'public'];
/**
 * How a research item opens. 'item' = the framed detail page (summary, metadata
 * and comments around the poster); 'poster' = the full-screen poster route, so
 * an HTML artifact reads as a standalone page. 'poster' only takes effect when
 * the item has body_html.
 */
export const RESEARCH_DEFAULT_VIEWS = ['item', 'poster'];
/** A resolved source reference attached to a research item (X link, article, etc). */
export const SourceRefSchema = z
    .object({
    source_type: z.string(), // 'x', 'web', 'linear', 'slack', ...
    source_ref: z.string(), // stable id/url the source is keyed by
    url: z.url().optional(),
    title: z.string().optional(),
    /** resolver output cached as-is (e.g. the daemon's X resolver payload) */
    resolved: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ResearchItemSchema = z
    .object({
    research_id: z.string().min(1),
    kind: z.enum(RESEARCH_KINDS),
    /** origin note, if this artifact was produced from a research note */
    note_id: z.string().nullable(),
    title: z.string(),
    /** stable url-safe slug; also the markdown filename in almanac-wiki */
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]*$/u),
    summary_md: z.string(),
    body_md: z.string(),
    /** full self-contained HTML "poster" document; rendered origin-isolated in a sandboxed iframe (null when the item is markdown-only) */
    body_html: z.string().nullable(),
    visibility: z.enum(RESEARCH_VISIBILITIES),
    default_view: z.enum(RESEARCH_DEFAULT_VIEWS),
    tags: z.array(z.string()),
    sources: z.array(SourceRefSchema),
    status: z.enum(RESEARCH_STATUSES),
    /** path within the almanac-wiki repo once published (null while draft/private) */
    git_path: z.string().nullable(),
    published_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
})
    .strict();
/** Follow-up comment thread on a research item (mirrors the note comment model). */
export const ResearchCommentSchema = z
    .object({
    comment_id: z.string().min(1),
    research_id: z.string().min(1),
    text_md: z.string(),
    author: z.string().nullable(),
    answered: z.boolean(),
    answer_md: z.string().nullable(),
    created_at: z.string(),
})
    .strict();
